import { ReactNode, useId, useState } from 'react';

import { GridPicker, type GridPickerItem } from '@/components/GridPicker';

const YEARS_PER_PAGE = 12;

const pageStart = (year: number) =>
  Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE;

type YearPickerProps = {
  value?: string;
  onChange?: (value: string) => void;
  label?: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-invalid'?: boolean;
};

const YearPicker = (props: YearPickerProps) => {
  const {
    value,
    onChange,
    label,
    hint,
    disabled,
    className,
    id,
    'aria-invalid': ariaInvalid,
  } = props;

  const generatedId = useId();
  const pickerId = id ?? generatedId;

  const [isOpen, setIsOpen] = useState(false);

  const selectedYear = value ? Number(value) : undefined;

  const [viewStart, setViewStart] = useState(() =>
    pageStart(selectedYear ?? new Date().getFullYear()),
  );

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setViewStart(pageStart(selectedYear ?? new Date().getFullYear()));
    }
    setIsOpen(open);
  };

  const onYearSelect = (year: number) => {
    onChange?.(String(year));
    setIsOpen(false);
  };

  const items: GridPickerItem[] = Array.from(
    { length: YEARS_PER_PAGE },
    (_, i) => {
      const year = viewStart + i;
      return {
        key: year,
        label: String(year),
        isSelected: selectedYear === year,
        onSelect: () => onYearSelect(year),
      };
    },
  );

  return (
    <GridPicker
      id={pickerId}
      label={label}
      hint={hint}
      isInvalid={ariaInvalid}
      disabled={disabled}
      className={className}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      displayValue={value ?? ''}
      title={`${viewStart} – ${viewStart + YEARS_PER_PAGE - 1}`}
      onPrevious={() => setViewStart((s) => s - YEARS_PER_PAGE)}
      onNext={() => setViewStart((s) => s + YEARS_PER_PAGE)}
      items={items}
    />
  );
};

export { YearPicker };
export type { YearPickerProps };
